import { useState } from "react";
import { motion, type Variants } from "framer-motion";
import { nanoid } from "nanoid";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";

const i18nRoot = "eoy_recap_2025.navigation_tabs";

const TABS = [
  t([i18nRoot, "community_growth"]),
  t([i18nRoot, "member_engagement"]),
  t([i18nRoot, "content_and_experience"]),
  t([i18nRoot, "business_impact"]),
  t([i18nRoot, "email_highlights"]),
  t([i18nRoot, "automation_and_efficiency"]),
];

const TAB_IDS = TABS.map(() => nanoid());

const STAGGER_CHILDREN = 0.05;
const DELAY_CHILDREN = 0.1;

const containerVariants: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],

      staggerChildren: STAGGER_CHILDREN,
      delayChildren: DELAY_CHILDREN,
    },
  },
};

const tabVariants: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const activeTabSpring = {
  type: "spring" as const,
  stiffness: 380,
  damping: 30,
};

export const NavigationTabs = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  return (
    <div
      className="fixed z-20 w-full max-w-6xl mx-auto px-6 pt-8 left-1/2 -translate-x-1/2"
      role="tablist"
    >
      <motion.div
        className="flex flex-wrap items-center justify-center gap-2"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {TABS.map((tab, index) => (
          <motion.button
            key={TAB_IDS[index]}
            id={TAB_IDS[index]}
            className={classNames(
              "relative px-5 py-3 rounded-full text-sm font-medium transition-colors",
              {
                "text-white": index === selectedTab,
                "text-white/70 hover:text-white": index !== selectedTab,
              },
            )}
            onClick={() => setSelectedTab(index)}
            variants={tabVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-selected={index === selectedTab}
            aria-controls={`tabpanel-${index}`}
            role="tab"
          >
            {index === selectedTab && (
              <motion.div
                layoutId="activeTab"
                className="absolute inset-0 bg-white/10 border border-[#7079B4] rounded-full"
                transition={activeTabSpring}
              />
            )}
            <span className="relative z-10">{tab}</span>
          </motion.button>
        ))}
      </motion.div>
    </div>
  );
};
